import { serviceCatalogue, servicePath, servicePillars } from '@/content/services';
import { type IconKey } from '@/content/site';

import { type Brand, featuredBrands } from './brands';

/** Navigation as approved in docs/information-architecture.md §1. */

export type NavLink = { label: string; to: string; description?: string };

/** A titled column of links inside a mega-menu. */
export type NavGroup = { title: string; items: readonly NavLink[] };

export type NavMenu = {
  label: string;
  items: readonly NavLink[];
  /** Renders the menu as a mega-menu with a brand-tile grid. */
  brands?: readonly Brand[];
  /**
   * Renders the menu as a mega-menu of titled columns, above `items`.
   *
   * A third shape beside the plain list and the brand grid, and separate from
   * `brands` rather than a generalisation of it: a brand tile is a logo in a
   * white box and a group is a heading over text links, so one component
   * drawing both from a shared abstraction would be deciding which it was
   * anyway. A menu may not set both.
   */
  groups?: readonly NavGroup[];
};
export type NavItem = ({ kind: 'link' } & NavLink) | ({ kind: 'menu' } & NavMenu);

export const quoteCta: NavLink = { label: 'Get a Quote', to: '/quote' };

/**
 * The id the organisation enquiry form on /clients answers to, and the link
 * other pages reach it by.
 *
 * Declared here for the reason `quoteCta` is: a path one page links another
 * page by is navigation, and this file is where those are written down. The
 * alternative was the home page's client wall importing a constant out of
 * `pages/clients/content`, which would be the only page-to-page import in the
 * app and would make a page's local copy file part of another page's build.
 *
 * The anchor is split out from the link because /clients needs the bare id to
 * put on the band, and an `id` typed in one file with a `#id` typed in another
 * is a link that breaks silently the first time either is renamed.
 */
export const FLEET_QUOTE_ANCHOR = 'fleet-quote';
export const fleetQuoteCta: NavLink = {
  label: 'Get a fleet quotation',
  to: `/clients#${FLEET_QUOTE_ANCHOR}`,
};

/**
 * The header's second call to action (§32), beside the quotation one.
 *
 * It lives here rather than in `content/consultation.ts` for the same reason
 * `quoteCta` does: a route the chrome links to is navigation, and every other
 * path the header knows about is declared in this file. Keeping it here also
 * keeps `config/navigation` from importing page content, which would close a
 * cycle — the molecules barrel reaches back into this file for `NavMenu`.
 *
 * The label names the offer, not the meeting. "Book a Consultation" was an
 * obligation verb attached to a diary entry the site cannot make — there is no
 * calendar behind it (A16) — and it asked for a commitment before the visitor
 * had been given anything. "Get Free Advice" is a verb and the benefit, in
 * fifteen characters, and it promises only what the page delivers.
 *
 * The path is not the label. The pill is conversion copy and the URL is a
 * search term: "import advice" is what somebody types, and it keeps this page
 * clear of `/services/import-consultation`, which is the page that argues for
 * the service rather than the one that asks.
 *
 * The export is still called `consultationCta` on purpose. The service is
 * "Import consultation" — one of the services Kabex named (§59) — and the
 * enquiries carry `kind: 'consultation'`; only the words a visitor reads have
 * changed. See `content/consultation.ts`.
 */
export const consultationCta: NavLink = {
  label: 'Get Free Advice',
  to: '/free-import-advice',
};

export const brandPath = (b: Brand) => `/cars/${b.slug}`;

/**
 * The Services menu, built from the catalogue rather than retyped.
 *
 * Eight services in one flat list is a menu nobody reads to the bottom, so
 * the panel carries the catalogue's own three pillars as columns — the same
 * grouping, in the same order, as the page it opens. A service added to
 * `content/services.ts` appears here with no change to this file, which is
 * the whole reason it is derived: a hand-written menu is the copy that goes
 * stale first.
 */
const serviceGroups: readonly NavGroup[] = servicePillars.map((pillar) => ({
  title: pillar.title,
  items: serviceCatalogue
    .filter((service) => service.pillar === pillar.id)
    .map((service) => ({ label: service.title, to: servicePath(service) })),
}));

export const primaryNav: readonly NavItem[] = [
  { kind: 'link', label: 'Car Finder', to: '/import-a-car' },
  { kind: 'link', label: 'Accessories', to: '/accessories' },
  {
    kind: 'menu',
    label: 'Brands',
    brands: featuredBrands,
    items: [
      { label: 'All cars', to: '/cars', description: 'Every car we can import for you' },
      // The one entry in the header that is a filter rather than a page. It is
      // here because the menu above it is a wall of car makes, and Sinotruk is
      // not a car make: a visitor who came for a truck needs somewhere to go
      // that is not a grid of saloons. `body` is a choice, not a body type —
      // "commercial" stands for trucks and buses together (schemas/bodyChoices.ts).
      {
        label: 'Trucks & Commercials',
        to: '/cars?body=commercial',
        description: 'Sinotruk and other heavy units',
      },
    ],
  },
  {
    kind: 'menu',
    label: 'Services',
    groups: serviceGroups,
    items: [
      {
        label: 'All services',
        to: '/services',
        description: 'Everything an import needs, under one roof',
      },
    ],
  },
  {
    kind: 'menu',
    label: 'Explore',
    items: [
      { label: 'How It Works', to: '/how-it-works', description: 'From auction to your driveway' },
      { label: 'Import Guide', to: '/import-guide', description: 'Rules, timelines and paperwork' },
      {
        label: 'Destinations',
        to: '/destinations',
        // The description used to read "Cars we have delivered", which is what
        // the delivered-cars archive does; this page lists destinations.
        description: 'Named routes and regional delivery enquiries',
      },
      { label: 'Reviews', to: '/reviews', description: 'What our customers say' },
      // Inside this menu rather than at the top level: the header row is
      // already at its width budget at `lg` (see the Tools note below), and
      // the roster is a page a visitor goes looking for rather than one the
      // chrome needs to advertise.
      {
        label: 'Our Clients',
        to: '/clients',
        description: 'Institutions and companies that buy through us',
      },
      // {
      //   label: 'Delivered Cars',
      //   to: '/deliveries/gallery',
      //   description: 'Photos and videos of real handovers',
      // },
    ],
  },
  /**
   * The two calculators, in a menu rather than side by side at the top level.
   *
   * They were two of eight top-level items, and "Tax Calculator" and
   * "Drive-Home Cost" are the two widest labels in the row — about 280px of
   * pill between them. The desktop nav switches on at `lg` (1,200px), where
   * that row plus the logo and the right-hand cluster had already been
   * tightened once to fit (see `navItemSx`); adding the consultation pill to
   * the cluster needed the width back. One "Tools" pill costs about 75px and
   * returns the rest.
   *
   * It is a real grouping and not a space-saving dodge: both pages answer
   * "what will this car cost me", one in URA's terms and one in the
   * visitor's, and a reader who wants either usually wants both. The footer's
   * Tools column has listed them together since it was written.
   */
  // {
  //   kind: 'menu',
  //   label: 'Tools',
  //   items: [
  //     {
  //       label: 'Tax Calculator',
  //       to: '/tax-calculator',
  //       description: 'What URA will charge on your car',
  //     },
  //     {
  //       label: 'Drive-Home Cost',
  //       to: '/landed-cost-calculator',
  //       description: 'Everything it costs to get it on the road',
  //     },
  //   ],
  // },
  // {
  //   kind: 'menu',
  //   label: 'Company',
  //   items: [
  //     { label: 'About Kabex', to: '/about', description: 'Our story and values' },
  //     { label: 'Reviews', to: '/reviews', description: 'What our customers say' },
  //   ],
  // },
  { kind: 'link', label: 'About', to: '/about' },
  { kind: 'link', label: 'Tax Calculator', to: '/tax-calculator' },
  { kind: 'link', label: 'Delivered Cars', to: '/deliveries/gallery' },
  { kind: 'link', label: 'Contact', to: '/contact' },
];

export type FooterColumn = {
  title: string;
  icon: IconKey;
  links: readonly NavLink[];
  /** A closing "see more" link, styled as a call to action. */
  more?: NavLink;
};

/** The footer lists this many brands (in featured order), then links to the full stock. */
const FOOTER_BRAND_COUNT = 8;

export const footerColumns: readonly FooterColumn[] = [
  {
    title: 'Company',
    icon: 'building',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Our Clients', to: '/clients' },
      { label: 'Services', to: '/services' },
      { label: 'How It Works', to: '/how-it-works' },
      { label: 'Destinations', to: '/destinations' },
      { label: 'Delivered Cars', to: '/deliveries/gallery' },
      { label: 'Reviews', to: '/reviews' },
      { label: 'Contact', to: '/contact' },
      consultationCta,
    ],
  },
  {
    /**
     * Every service page, which is the footer earning its keep: it is
     * the site's main source of internal links on every page (IA §1), and
     * pages reachable only from one hub and one menu are pages a
     * crawler has to work to find. Derived from the catalogue, so it cannot
     * list a service that has no page.
     */
    title: 'Services',
    icon: 'consult',
    links: serviceCatalogue.map((service) => ({
      label: service.title,
      to: servicePath(service),
    })),
    more: { label: 'All services', to: '/services' },
  },
  {
    // Named as in the header menu (decided 2026-09-24).
    title: 'Brands',
    icon: 'car',
    links: featuredBrands
      .slice(0, FOOTER_BRAND_COUNT)
      .map((b) => ({ label: b.name, to: brandPath(b) })),
    more: { label: 'View all cars', to: '/cars' },
  },
  {
    title: 'Tools',
    icon: 'calculator',
    links: [
      { label: 'Tax Calculator', to: '/tax-calculator' },
      { label: 'Drive-Home Cost', to: '/landed-cost-calculator' },
      { label: 'Car Accessories', to: '/accessories' },
      { label: 'Import Guide', to: '/import-guide' },
    ],
  },
];

/** Cookie settings joins these in Phase 13. */
export const legalLinks: readonly NavLink[] = [
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
];
